import { MesfModal } from "@dexteel/mesf-core";
import { Grid, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { modalStyles } from "@/shared/styles/modalStyles";
import { FooterModalButtons } from "../../../../../../controls/buttons/FooterModalButtons";
import { hasInjectorDoor } from "../../../helpers/injection-helper";
import { useEAFDataEntryActions } from "../../../hooks/useEAFDataEntryActions";
import type { CarbonInjection } from "../../../models/eaf.models";

const MIN_INJECTION_VALUE = 0;
const DECIMAL_PATTERN = /^-?(?:\d+(?:\.\d*)?|\.\d+)$/;
const INJECTION_RULES = {
  validate: {
    numeric: (value: string) =>
      value.trim() === "" ||
      DECIMAL_PATTERN.test(value.trim()) ||
      "Value must be a valid number",
    nonNegative: (value: string) =>
      value.trim() === "" ||
      Number(value) >= MIN_INJECTION_VALUE ||
      "Value must be non-negative",
  },
} as const;

interface InjectionFormValues {
  Injector1: string;
  Injector2: string;
  Injector3: string;
  InjectorDoor: string;
}

const toFormValue = (value: number | null | undefined) =>
  value?.toString() ?? "";

const toInjectionValue = (value: string) => {
  const normalizedValue = value.trim();
  return normalizedValue === "" ? null : Number(normalizedValue);
};

interface Props {
  showModal: boolean;
  onHide: () => void;
  injection: CarbonInjection | undefined;
}

export const InjectionModal = ({ showModal, onHide, injection }: Props) => {
  const { saveInjection } = useEAFDataEntryActions();

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const { handleSubmit, control, reset } = useForm<InjectionFormValues>({
    defaultValues: {
      Injector1: "",
      Injector2: "",
      Injector3: "",
      InjectorDoor: "",
    },
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<InjectionFormValues> = async (data) => {
    if (!injection) {
      return;
    }

    setIsSubmitLoading(true);
    await saveInjection({
      ...injection,
      Injector1: toInjectionValue(data.Injector1),
      Injector2: toInjectionValue(data.Injector2),
      Injector3: toInjectionValue(data.Injector3),
      InjectorDoor: toInjectionValue(data.InjectorDoor),
    });
    setIsSubmitLoading(false);
    onHide();
  };

  useEffect(() => {
    if (showModal) {
      reset({
        Injector1: toFormValue(injection?.Injector1),
        Injector2: toFormValue(injection?.Injector2),
        Injector3: toFormValue(injection?.Injector3),
        InjectorDoor: toFormValue(injection?.InjectorDoor),
      });
    }
  }, [injection, reset, showModal]);

  return (
    <>
      <MesfModal
        title={`EDIT ${injection?.Type.toUpperCase()} INJECTION`}
        open={showModal}
        handleClose={() => onHide()}
        id="injection-modal"
        maxWidth="sm"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MesfModal.Content dividers sx={modalStyles.modalContent}>
            <Grid container spacing={1}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="Injector1"
                    control={control}
                    rules={INJECTION_RULES}
                    render={({
                      field: { ref: _ref, ...fieldProps },
                      fieldState: { error },
                    }) => (
                      <TextField
                        {...fieldProps}
                        fullWidth
                        label="Injector 1"
                        variant="outlined"
                        type="text"
                        error={Boolean(error)}
                        helperText={error?.message}
                        sx={{ minWidth: "50px" }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.currentTarget.blur();
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="Injector2"
                    control={control}
                    rules={INJECTION_RULES}
                    render={({
                      field: { ref: _ref, ...fieldProps },
                      fieldState: { error },
                    }) => (
                      <TextField
                        {...fieldProps}
                        fullWidth
                        label="Injector 2"
                        variant="outlined"
                        type="text"
                        error={Boolean(error)}
                        helperText={error?.message}
                        sx={{ minWidth: "50px" }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.currentTarget.blur();
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="Injector3"
                    control={control}
                    rules={INJECTION_RULES}
                    render={({
                      field: { ref: _ref, ...fieldProps },
                      fieldState: { error },
                    }) => (
                      <TextField
                        {...fieldProps}
                        fullWidth
                        label="Injector 3"
                        variant="outlined"
                        type="text"
                        error={Boolean(error)}
                        helperText={error?.message}
                        sx={{ minWidth: "50px" }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.currentTarget.blur();
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                {injection && hasInjectorDoor(injection.Type) ? (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="InjectorDoor"
                      control={control}
                      rules={INJECTION_RULES}
                      render={({
                        field: { ref: _ref, ...fieldProps },
                        fieldState: { error },
                      }) => (
                        <TextField
                          {...fieldProps}
                          fullWidth
                          label="Injector Door"
                          variant="outlined"
                          type="text"
                          error={Boolean(error)}
                          helperText={error?.message}
                          sx={{ minWidth: "50px" }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.currentTarget.blur();
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </Grid>
          </MesfModal.Content>
          <MesfModal.Actions sx={modalStyles.modalActions}>
            <FooterModalButtons
              isSubmitLoading={isSubmitLoading}
              onHide={onHide}
            />
          </MesfModal.Actions>
        </form>
      </MesfModal>
    </>
  );
};
